import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getLeadById, patchLead, logError } from '../api';
import { isComercial, isAdmin, isPropietario } from '../services/AuthService';
import LeadEventModal from './LeadEventModal';
import LeadEditModal from './LeadEditModal';
import LeadEventsRow from './LeadEventsRow';

const LeadDetailModal = ({ show, handleClose, leadId, onLeadUpdated }) => {
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showEventModal, setShowEventModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [updatingUrgent, setUpdatingUrgent] = useState(false);

    useEffect(() => {
        if (show && leadId) {
            loadLead();
        }
    }, [show, leadId]);

    const loadLead = async () => {
        setLoading(true);
        try {
            const response = await getLeadById(leadId);
            setLead(response);
        } catch (error) {
            logError(error);
            console.error('Error loading lead:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEventAdded = () => {
        loadLead();
    };

    const handleLeadUpdated = () => {
        loadLead();
    };

    const handleToggleUrgent = async () => {
        setUpdatingUrgent(true);
        try {
            await patchLead(leadId, { 
                isUrgent: !lead.isUrgent,
                statusId: lead.statusId
            });
            await loadLead();
            if (onLeadUpdated) {
                await onLeadUpdated();
            }
        } catch (error) {
            logError(error);
            console.error('Error updating urgent status:', error);
        } finally {
            setUpdatingUrgent(false);
        }
    };

    const canEditFields = () => {
        return (!lead?.name || lead.name.trim() === '') || (!lead?.zipCode || lead.zipCode.trim() === '');
    };

    const isSuperUser = () => {
        return isAdmin() || isPropietario();
    };

    const handleEditLead = () => {
        window.open(`/lead-assignment/edit/${leadId}`, '_blank');
    };

    const getStatusColor = (statusId) => {
        const colors = {
            1: '#ffc107', 2: '#17a2b8', 3: '#28a745', 4: '#dc3545'
        };
        return colors[statusId] || '#6c757d';
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {loading ? 'Cargando...' : (lead?.name || 'Lead sin nombre')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <div className="text-center p-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={8}>
                                    <div className="mb-2">
                                        <strong>📞 Teléfono:</strong> {lead?.phone || 'Sin teléfono'}
                                    </div>
                                    <div className="mb-2">
                                        <strong>📍 Código Postal:</strong> {lead?.zipCode || 'Sin código postal'}
                                    </div>
                                    <div className="mb-2">
                                        <strong>🌐 Origen:</strong> {lead?.origin?.name || 'Sin origen'}
                                    </div>
                                    <div className="mb-2">
                                        <strong>📊 Estado:</strong> 
                                        {lead?.status?.name ? (
                                            <Badge 
                                                className="ms-2"
                                                style={{ 
                                                    backgroundColor: getStatusColor(lead.statusId), 
                                                    color: 'white' 
                                                }}
                                            >
                                                {lead.status.name}
                                            </Badge>
                                        ) : 'Sin estado'}
                                    </div>
                                    <div className="mb-2 d-flex align-items-center">
                                        <strong>⚡ Urgente:</strong>
                                        <span 
                                            className={`ms-2 badge ${lead?.isUrgent ? 'bg-warning text-dark' : 'bg-light text-muted'} urgent-toggle`}
                                            onClick={handleToggleUrgent}
                                            style={{ cursor: updatingUrgent ? 'wait' : 'pointer' }}
                                        >
                                            {updatingUrgent ? (
                                                <span className="spinner-border spinner-border-sm me-1" role="status" style={{ width: '12px', height: '12px' }}></span>
                                            ) : (
                                                <i className={`bi ${lead?.isUrgent ? 'bi-lightning-fill' : 'bi-lightning'} me-1`}></i>
                                            )}
                                            {lead?.isUrgent ? 'SÍ' : 'NO'}
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-center p-3 rounded bg-warning text-dark">
                                        <div className="fw-bold mb-1">📅 Seguimiento</div>
                                        <div className="fw-bold">
                                            {lead?.followUpDate ? new Date(lead.followUpDate).toLocaleDateString() : 'Sin programar'}
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {lead?.comments && (
                                <Row className="mb-3">
                                    <Col>
                                        <div className="bg-light p-3 rounded">
                                            <strong>💬 Comentarios:</strong>
                                            <p className="mb-0 mt-2">{lead.comments}</p>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            <Row className="mb-3">
                                <Col>
                                    <div className="text-center mb-3">
                                        <Button 
                                            variant="success" 
                                            size="lg"
                                            onClick={() => setShowEventModal(true)}
                                            className="px-4 py-2 fw-bold"
                                            style={{ 
                                                fontSize: '1.1rem',
                                                boxShadow: '0 4px 8px rgba(40, 167, 69, 0.3)',
                                                border: 'none'
                                            }}
                                        >
                                            <i className="bi bi-plus-circle-fill me-2" style={{ fontSize: '1.2rem' }}></i>
                                            Añadir Registro
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                            
                            <Row>
                                <Col>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">📅 Registros ({lead?.leadRegistries?.length || 0})</h6>
                                    </div>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <LeadEventsRow registries={lead?.leadRegistries || []} />
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {isSuperUser() ? (
                        <Button 
                            variant="warning"
                            size="sm"
                            onClick={handleEditLead}
                        >
                            <i className="bi bi-pencil-fill me-1"></i>
                            Editar Datos
                        </Button>
                    ) : canEditFields() && (
                        <Button 
                            variant="warning"
                            size="sm"
                            onClick={() => setShowEditModal(true)}
                        >
                            <i className="bi bi-pencil-fill me-1"></i>
                            Completar Datos
                        </Button>
                    )}
                    {isComercial() && (
                        <Button 
                            variant="success" 
                            onClick={() => {
                                handleClose();
                                navigate('/orders/new', { state: { leadData: lead } });
                            }}
                        >
                            Crear Pedido
                        </Button>
                    )}
                    <Button variant="secondary" onClick={handleClose}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            <LeadEventModal
                show={showEventModal}
                handleClose={() => setShowEventModal(false)}
                leadId={leadId}
                onEventAdded={handleEventAdded}
            />
            
            <LeadEditModal
                show={showEditModal}
                handleClose={() => setShowEditModal(false)}
                lead={lead}
                onUpdate={handleLeadUpdated}
            />
        </>
    );
};

export default LeadDetailModal;